"use client";

import { useState, useContext } from "react";
import { useRouter } from "next/navigation";
import { MenuItem, Stack, Typography } from "@mui/material";
import { NotificationsContext } from "@lib/notifications/NotificationsContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { IconButton, NotificationList } from "@/ui";
import ContextMenu from "@components/shared/ContextMenu";
import Icon from "@components/Notifications/Icon";
import hydrateNotification from "@lib/notifications/hydrateNotification";
import { getAccent, getActionHref, getActionLabel } from "@lib/notifications/notificationDisplay";
import moment from "moment";
import { NotificationTypes } from "@lib/enums";

const FILTER_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "email", label: "Email opened", types: [NotificationTypes.EMAIL_OPENED] },
  { key: "views", label: "Viewed", types: [NotificationTypes.PAGE_VIEWED, NotificationTypes.CANDIDATE_VIEW_MORE] },
  { key: "interested", label: "Interested", types: [NotificationTypes.CANDIDATE_IS_INTERESTED] },
  { key: "interview", label: "Interview requested", types: [NotificationTypes.CANDIDATE_BOOK_INTERVIEW] },
  { key: "feedback", label: "Feedback", types: [NotificationTypes.CANDIDATE_ASK_QUESTION, NotificationTypes.CANDIDATE_GIVE_FEEDBACK, NotificationTypes.CANDIDATE_NOT_INTERESTED] },
  { key: "applications", label: "Job applications", types: [NotificationTypes.JOB_APPLIED, NotificationTypes.JOB_APPLY_CLICKED] },
  { key: "bounced", label: "Bounced", types: [NotificationTypes.EMAIL_BOUNCED] },
  { key: "unsubscribed", label: "Unsubscribed", types: [NotificationTypes.EMAIL_UNSUBSCRIBED] },
];


export default function Notifications({ changeRoute }) {
  const router = useRouter();
  const { notifications, isFetching, dismiss } = useContext(NotificationsContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filter, setFilter] = useState("all");

  if (isFetching) return null;
  if (!notifications?.length) return null;

  const filtered = filter === "all"
    ? notifications
    : notifications.filter((n) => {
        const cat = FILTER_CATEGORIES.find((c) => c.key === filter);
        return cat?.types?.includes(n.type);
      });

  const items = filtered.map((n) => {
    const { accentColor, iconBg } = getAccent(n.type);

    // Null when the notification's float/submission is gone — there's nowhere
    // to send the user, so the action and its menu entry are both dropped.
    const href = getActionHref(n);
    const actionLabel = getActionLabel(n);
    const handleView = href ? () => changeRoute(href) : undefined;

    return {
      icon: <Icon type={n.type} />,
      accentColor,
      iconBg,
      date: moment(n.created_at).fromNow(),
      content: hydrateNotification(n),
      isUnread: !n.read,
      actionLabel: actionLabel ?? undefined,
      onAction: handleView,
      menuItems: [
        ...(actionLabel ? [{ label: actionLabel, onClick: handleView }] : []),
        { label: 'Dismiss', onClick: () => dismiss(n.id), destructive: true },
      ],
    };
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" gap="var(--space-3)">
        <Typography variant="h2">
          Notifications {FILTER_CATEGORIES.find((c) => c.key === filter)?.label.toLowerCase()}
        </Typography>
        <IconButton
          size="sm"
          id="notifications-filter-btn"
          aria-label="Filter notifications"
          aria-haspopup="true"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: "1.25rem", color: "var(--action-primary)" }} />
        </IconButton>
      </Stack>
      <ContextMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        labelledBy="notifications-filter-btn"
      >
        {FILTER_CATEGORIES.map((cat) => (
          <MenuItem
            key={cat.key}
            selected={filter === cat.key}
            onClick={() => { setFilter(cat.key); setAnchorEl(null); }}
          >
            {cat.label}
          </MenuItem>
        ))}
      </ContextMenu>
      <NotificationList
        items={items}
        onShowAll={() => router.push("/notifications")}
        emptyMessage={`No ${filter === "all" ? "" : FILTER_CATEGORIES.find(c => c.key === filter)?.label.toLowerCase() + " "}notifications`}
      />
    </Stack>
  );
}
